// Zone-first prize INTAKE: the model behind the console's New Submission dialog.
//
// This is a port of the admin v2 submission form's inline helpers
// (admin-dashboard/public/v2/index.html, markers [v2-zone-options],
// [v2-slot-conflicts], [v2-zone-picker], [v2-slot-wire]). Those were written
// framework-free precisely so they could be ported rather than reinvented; the
// block comments below are theirs, kept so the two forms can be diffed as one
// spec. Behaviour is identical except for the fail-closed rule called out at
// tierDecorationView.
//
// ZONE-FIRST INTAKE (Grant 2026-08-11): "select the product and the placement
// zone(s) and then upload a logo to each placement zone or select if they plan
// on having custom names, custom numbers, stacked name and number in that
// placement zone." Everything here is DATA-driven — the zone list, the grouping,
// the kinds and the stacked pair all come from platformConfig/decorationLocations
// (GET /api/decoration-locations) intersected with the product's printAreas.
// There is no hardcoded location code, kind, view or product id anywhere in this
// file, and there must never be one.
//
// Everything is pure and DOM-free: the client has no jsdom, so the component
// stays dumb and every rule is unit tested here.
import {
  type DecorationRegistry, type DecorationLocation, type DecorationKind,
  type MockupViewName, type PrintArea, type PrizeProduct,
  isTextFillKind,
  isPrizeFont, resolvePrizeFont, resolvePrizeSlotInk, isPrizeSlotInk,
} from './prize-product';

/**
 * `stacksWith` names the ONE location this one forms a name-over-number STACK
 * with (BN <-> BC today) — see shared/decoration-locations.js. It is absent from
 * ./prize-product.ts's DecorationLocation, which the prize-product EDITOR owns
 * and this module may not modify, so it is layered on here rather than by
 * widening a type another surface depends on.
 */
export type StackableLocation = DecorationLocation & { stacksWith?: string };

/** One offerable print zone: a registry location ∩ one of the product's areas. */
export type ZoneOption = {
  code: string;
  label: string;
  view: MockupViewName | null;
  sort: number;
  /** Registry `standard: true` — the picker lists these first. Never a list here. */
  standard: boolean;
  stacksWith: string | null;
  /** Kinds this PRODUCT can actually do at this zone (text needs a font). */
  kinds: DecorationKind[];
  area: PrintArea;
  /** `3.5" × 3"`, or '' when the product declares no usable size. */
  size: string;
  /** Vertical position in the garment view; the stacked pair's ordering authority. */
  mockupY: number | null;
};

/**
 * A chosen slot, in row order. Index 0 is the tier's primary logo.
 *
 * `decoration` is ONLY meaningful on a `kind: 'logo'` row — a chest logo and a
 * sleeve logo on the same tier can each want their own caption at intake
 * ("GO KNIGHTS" / "EST. 2026"), which one shared tier-wide text field can't
 * express. When text is present, addtext also needs `font` + `textColor`
 * (SLOT_TEXT_REQUIRED_INPUTS). Absent is the common case.
 */
export type IntakeSlot = {
  code: string;
  kind: DecorationKind;
  font?: string;
  ink?: string;
  decoration?: { text: string; font?: string; textColor?: string };
};

export const ADDTEXT_FONTS = ['default', 'zingrustbase', 'akrobatblack', 'zeroathletics'] as const;
export const ADDTEXT_PICKER_FONTS = ['zingrustbase', 'akrobatblack', 'zeroathletics'] as const;
export const CAPTION_COLOR_MODES = ['auto', 'auto-light'] as const;
export const ADDTEXT_FONT_LABELS: Record<(typeof ADDTEXT_FONTS)[number], string> = {
  default: 'Zing Rust Demo Base (legacy default)',
  zingrustbase: 'zingrustbase — Zing Rust Demo Base',
  akrobatblack: 'akrobatblack — Akrobat Black',
  zeroathletics: 'zeroathletics — Zero Athletics',
};
export const CAPTION_COLOR_CHOICES = [
  { value: 'auto', label: 'Darkest logo color' },
  { value: 'auto-light', label: 'Lightest logo color' },
  { value: '#FFFFFF', label: 'White' },
  { value: '#000000', label: 'Black' },
] as const;
export const DEFAULT_CAPTION_FONT = 'zingrustbase';
export const DEFAULT_CAPTION_COLOR = 'auto';

export function addtextFontLabel(name: string): string {
  const key = String(name || '').trim();
  return (ADDTEXT_FONT_LABELS as Record<string, string>)[key] || key;
}

export const ADDTEXT_FONT_PREVIEW_LABELS: Record<(typeof ADDTEXT_FONTS)[number], string> = {
  default: 'Zing Rust',
  zingrustbase: 'Zing Rust',
  akrobatblack: 'Akrobat Black',
  zeroathletics: 'Zero Athletics',
};

export function addtextFacePreviewLabel(name: string): string {
  const key = String(name || '').trim();
  return (ADDTEXT_FONT_PREVIEW_LABELS as Record<string, string>)[key] || addtextFontLabel(key) || key;
}

export function pickerFontValue(name?: string): string {
  const key = String(name || '').trim();
  if (!key || key === 'default') return DEFAULT_CAPTION_FONT;
  return key;
}

export function addtextFaceClass(name: string): string {
  const key = pickerFontValue(name);
  return `addtext-face addtext-face-${key}`;
}

export function addtextFaceFamily(name: string): string {
  const key = pickerFontValue(name);
  if (key === 'default') return 'Addtext-default, Addtext-zingrustbase, sans-serif';
  if (key === 'akrobatblack') return 'Addtext-akrobatblack, sans-serif';
  if (key === 'zingrustbase') return 'Addtext-zingrustbase, sans-serif';
  if (key === 'zeroathletics') return 'Addtext-zeroathletics, sans-serif';
  return `Addtext-${key}, sans-serif`;
}

export function addtextFontFaceCss(names: string[] = [...ADDTEXT_FONTS]): string {
  return names.map((name) => (
    `@font-face{font-family:'Addtext-${name}';src:url('/api/fonts/file/${encodeURIComponent(name)}');font-display:swap;}`
  )).join('');
}

const ADDTEXT_FACE_STYLE_ID = 'addtext-font-faces';

export function ensureAddtextFontFaces(names: string[] = [...ADDTEXT_PICKER_FONTS]): void {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(ADDTEXT_FACE_STYLE_ID);
  if (!el) {
    el = document.createElement('style');
    el.id = ADDTEXT_FACE_STYLE_ID;
    document.head.appendChild(el);
  }
  const css = addtextFontFaceCss(names);
  if (el.textContent !== css) el.textContent = css;
}

/** Picker fonts first; extras from /fonts never replace them or reintroduce default. */
export function mergeAddtextFonts(apiNames: string[] = []): string[] {
  const names: string[] = [...ADDTEXT_PICKER_FONTS];
  for (const raw of apiNames) {
    const name = String(raw || '').trim();
    if (!name || name === 'default' || names.includes(name)) continue;
    names.push(name);
  }
  return names;
}

export function captionColorChoice(raw?: string): string {
  const trimmed = String(raw || '').trim();
  const lower = trimmed.toLowerCase();
  if (!trimmed || lower === 'auto') return 'auto';
  if (lower === 'auto-light') return 'auto-light';
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed.toUpperCase();
  return 'auto';
}

export function isLegacyCaptionHex(raw?: string): boolean {
  const hex = captionColorChoice(raw);
  return /^#[0-9A-F]{6}$/.test(hex) && hex !== '#FFFFFF' && hex !== '#000000';
}

/** Slot index (>0) -> the artwork chosen for it. Text slots never appear here. */
export type SlotFiles = Record<number, File>;

export type TierDecorationView =
  | { mode: 'legacy' }
  | { mode: 'zones'; primary: string }
  | { mode: 'error'; error: string };

export type StackPair = {
  view: MockupViewName | null;
  ordered: { code: string; label: string; kind: DecorationKind }[];
};

const locationsOf = (reg: DecorationRegistry | undefined): Record<string, StackableLocation> =>
  (reg?.locations || {}) as Record<string, StackableLocation>;

// ---------------------------------------------------------------------------
// Zone options  (port of [v2-zone-options])
// ---------------------------------------------------------------------------

/**
 * Mirrors productconfig's derivePrintAreas: a product's explicit printAreas, or
 * the single area a legacy product's singular `placement` resolves to through
 * the registry's placementAlias. Same derivation submitlogo runs server-side, so
 * the form can never offer a code intake would reject.
 */
export function productPrintAreas(
  product: PrizeProduct | null | undefined,
  reg: DecorationRegistry | undefined,
): Record<string, PrintArea> {
  const locations = locationsOf(reg);
  if (!product || !Object.keys(locations).length) return {};
  if (product.printAreas && Object.keys(product.printAreas).length) return product.printAreas;
  const hit = Object.values(locations).find((l) => l.placementAlias === product.placement);
  if (!hit) return {};
  return { [hit.code]: { widthInches: product.production?.width, heightInches: product.production?.height } };
}

/**
 * The print size as it reads on a spec sheet. A print area with no usable
 * dimensions renders NOTHING — a legacy product with no production block has no
 * number to show, and inventing one would misstate what prints.
 */
export function zoneSizeLabel(area: PrintArea | undefined): string {
  const w = Number(area?.widthInches);
  const h = Number(area?.heightInches);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return '';
  const n = (v: number) => Math.round(v * 100) / 100;
  return `${n(w)}" × ${n(h)}"`;
}

/**
 * What a TEXT zone will actually print, straight off the product's print area
 * (productconfig/lib/decoration.js buildDecorationLayers reads the same three
 * fields). Only CONFIGURED values are shown: maxChars/maxDigits/ink have
 * service-side defaults, and restating them in the browser would be a second
 * copy of a platform constant that could drift out from under the operator.
 */
export function zoneTextSpec(opt: ZoneOption | undefined, kind: DecorationKind): string {
  if (kind !== 'name' && kind !== 'number') return '';
  const a = opt?.area || ({} as PrintArea);
  const bits: string[] = [];
  if (a.font) bits.push(String(a.font));
  if (kind === 'name' && Number.isFinite(a.maxChars)) bits.push(`max ${a.maxChars} characters`);
  if (kind === 'number' && Number.isFinite(a.maxDigits)) bits.push(`max ${a.maxDigits} digits`);
  if (a.ink) bits.push(`${a.ink} ink`);
  return bits.join(' · ');
}

/**
 * The offerable zones for a product: registry ∩ derived print areas, with text
 * kinds available only where the product configured typography.
 *
 * NO FONT => NO TEXT. productconfig refuses to build a name/number layer without
 * one and submitlogo 400s at intake ("print area has no font"), so offering it
 * would be offering something that cannot be sold. This is what keeps a
 * legs-only product (black-jogger: LL/RL logos, no typography) from showing a
 * name or number option — a rule about DATA, not about that product's id.
 *
 * `standard` and `stacksWith` ride along untouched — the picker groups and pairs
 * on them and must never re-derive either from a code.
 */
export function slotOptions(
  product: PrizeProduct | null | undefined,
  reg: DecorationRegistry | undefined,
): ZoneOption[] {
  const locations = locationsOf(reg);
  const areas = productPrintAreas(product, reg);
  return Object.entries(areas)
    .map(([code, area]) => ({ code, area, loc: locations[code] }))
    .filter((o) => o.loc && o.loc.active !== false && Array.isArray(o.loc.kinds))
    .map(({ code, area, loc }) => ({
      code,
      label: loc.label,
      view: loc.view || null,
      sort: Number(loc.sort) || 0,
      standard: loc.standard === true,
      stacksWith: typeof loc.stacksWith === 'string' && loc.stacksWith ? loc.stacksWith : null,
      kinds: loc.kinds.filter((k) => k === 'logo' || !!area.font),
      area,
      size: zoneSizeLabel(area),
      mockupY: Number.isFinite(area.mockup?.y) ? (area.mockup!.y as number) : null,
    }))
    .filter((o) => o.kinds.length)
    .sort((a, b) => a.sort - b.sort);
}

/**
 * What the tier's decoration section shows. Pure, so the LEGACY guarantee is
 * testable: a product with a single derived print area (every un-migrated
 * product) is mode 'legacy' — no zone picker, no slots on the wire, exactly
 * today's single-upload form.
 *
 * FAIL CLOSED (differs from the admin form this is ported from). When the
 * product HAS configured printAreas but the registry could not be read, this
 * returns 'error', not 'legacy'-with-a-warning. Downgrading would submit a
 * multi-zone product as a single-logo legacy submission: submitlogo takes the
 * legacy path whenever no slot field is sent, so the result is a valid-LOOKING
 * submission that silently drops every zone past the first. An outage must block
 * the operator, never quietly change what they are ordering.
 */
export function tierDecorationView(
  product: PrizeProduct | null | undefined,
  opts: ZoneOption[],
  registryError: string | null | undefined,
): TierDecorationView {
  const configured = !!(product?.printAreas && Object.keys(product.printAreas).length);
  if (configured && registryError) {
    return {
      mode: 'error',
      error: `Decoration locations are unavailable (${registryError}). This product has configured print zones, `
        + 'so it cannot be submitted until they load — submitting now would drop every zone but the first.',
    };
  }
  if (!opts || opts.length <= 1) return { mode: 'legacy' };
  // Slot 0 is the tier's primary logo by contract (submitlogo rejects a non-logo
  // slot 0). A multi-zone product where NO zone can take a logo cannot be
  // submitted in slot mode — say so instead of seeding a slot intake will 400.
  const logos = opts.filter((o) => o.kinds.includes('logo'));
  if (!logos.length) {
    return {
      mode: 'error',
      error: 'This product has print zones but none of them accepts a logo — the first zone must be the primary logo.',
    };
  }
  // Default primary: the first STANDARD logo zone in registry sort order, or the
  // first logo zone if the product has no standard one. The operator can change
  // it; this only decides what the picker opens on.
  return { mode: 'zones', primary: (logos.find((o) => o.standard) || logos[0]).code };
}

// ---------------------------------------------------------------------------
// Zone conflicts  (port of [v2-slot-conflicts])
// ---------------------------------------------------------------------------
// A garment may OFFER Full Front AND the chest logos — every one of our tops
// does — but they print over the same chest real estate, so ONE submission uses
// one side or the other. Mirrors shared/decoration-locations.js conflictsFor /
// conflictingPairs. submitlogo is the authoritative gate; this only stops an
// operator walking into its 400.
//
// ./prize-product.ts exports a conflictsFor/conflictingPairs pair too, but those
// THROW on an unrecognised code — right for the product editor, wrong here,
// where the picker sweeps every code the API happened to return.

/** The codes `code` may not share a submission with. Unknown code => none. */
export function zoneConflictsFor(reg: DecorationRegistry | undefined, code: string): string[] {
  const loc = locationsOf(reg)[code];
  return Array.isArray(loc?.conflictsWith) ? loc.conflictsWith : [];
}

/**
 * True when the two codes exclude each other. EITHER side declaring it counts:
 * the server refuses an asymmetric registry at boot, but this form renders
 * whatever the API returned, and reading only one direction would silently
 * switch the rule off in the UI.
 */
export function zoneCodesConflict(reg: DecorationRegistry | undefined, a: string, b: string): boolean {
  return zoneConflictsFor(reg, a).includes(b) || zoneConflictsFor(reg, b).includes(a);
}

/** Codes excluded by what OTHER rows have already chosen. */
export function blockedCodes(
  reg: DecorationRegistry | undefined,
  slots: IntakeSlot[],
  exceptIndex: number,
): Set<string> {
  const blocked = new Set<string>();
  (slots || []).forEach((s, i) => {
    if (i === exceptIndex || !s?.code) return;
    for (const code of Object.keys(locationsOf(reg))) {
      if (zoneCodesConflict(reg, code, s.code)) blocked.add(code);
    }
  });
  return blocked;
}

/** The conflicting pairs actually present, [a, b] in row order. */
export function slotConflictPairs(
  reg: DecorationRegistry | undefined,
  slots: IntakeSlot[],
): [string, string][] {
  const codes = (slots || []).map((s) => s?.code).filter(Boolean) as string[];
  const pairs: [string, string][] = [];
  for (let i = 0; i < codes.length; i += 1) {
    for (let j = i + 1; j < codes.length; j += 1) {
      if (zoneCodesConflict(reg, codes[i], codes[j])) pairs.push([codes[i], codes[j]]);
    }
  }
  return pairs;
}

/**
 * Word-for-word what submitlogo answers (decoration-slots.js), so the operator
 * does not read two different sentences about the same rule.
 */
export function slotConflictMessage(
  reg: DecorationRegistry | undefined,
  pair: [string, string],
): string {
  const label = (code: string) => locationsOf(reg)[code]?.label || code;
  return `${label(pair[0])} (${pair[0]}) cannot be combined with ${label(pair[1])} (${pair[1]})`
    + ' — they print over each other. Choose one.';
}

// ---------------------------------------------------------------------------
// The zone picker  (port of [v2-zone-picker])
// ---------------------------------------------------------------------------

/**
 * The zones this product can still take: in the product's print areas, not
 * already chosen, and not excluded by a zone that IS already chosen. Conflict
 * filtering is the same blockedCodes the row dropdowns use — a zone the operator
 * may not combine is never offered in the first place.
 */
export function zoneOfferable(
  reg: DecorationRegistry | undefined,
  opts: ZoneOption[],
  slots: IntakeSlot[],
): ZoneOption[] {
  const taken = new Set((slots || []).map((s) => s?.code).filter(Boolean));
  const blocked = blockedCodes(reg, slots, -1);
  return (opts || []).filter((o) => !taken.has(o.code) && !blocked.has(o.code));
}

/**
 * Standard zones first, then the rest. Which zones are "standard" is DATA
 * (registry `standard: true`) — never a list in this file. `opts` is already in
 * registry sort order, so partitioning preserves it.
 */
export function zoneGroups(
  reg: DecorationRegistry | undefined,
  opts: ZoneOption[],
  slots: IntakeSlot[],
): { standard: ZoneOption[]; other: ZoneOption[] } {
  const free = zoneOfferable(reg, opts, slots);
  return { standard: free.filter((o) => o.standard), other: free.filter((o) => !o.standard) };
}

/**
 * The "Name + number (stacked)" one-click preset.
 *
 * A roster prize decorates ONE garment view with two separate prints: the
 * player's last name above the player's number. Operators read that as a single
 * option, so it is one control — but slot codes are UNIQUE per tier, so it is
 * two zones, two print areas and two DTF prints, and it materializes as TWO
 * slots. The copy says so.
 *
 * The pair is DECLARED by the registry's `stacksWith`, never inferred from
 * view + kinds — mirrors requestedit/lib/slot-edit.js stackedNameNumberPreset so
 * intake and the edit form agree on what a stack is. Only MUTUALLY declared
 * codes pair: a half-declared `stacksWith` is not a pair. Geometry only decides
 * which half sits on top: mockup.y when both areas declare one, registry sort
 * otherwise. Offered only when BOTH halves are still free.
 */
export function zoneStackPair(
  reg: DecorationRegistry | undefined,
  opts: ZoneOption[],
  slots: IntakeSlot[],
): StackPair | null {
  const free = zoneOfferable(reg, opts, slots);
  const byCode = new Map(free.map((o) => [o.code, o]));
  for (const a of free) {
    if (!a.stacksWith) continue;
    const b = byCode.get(a.stacksWith);
    if (!b || b.code === a.code) continue;
    if (b.stacksWith !== a.code) continue;   // half-declared pair is not a pair
    if (a.view !== b.view) continue;         // "stacked" is within one garment view
    const name = [a, b].find((o) => o.kinds.includes('name'));
    const number = [a, b].find((o) => o !== name && o.kinds.includes('number'));
    if (!name || !number) continue;
    const bothY = Number.isFinite(name.mockupY) && Number.isFinite(number.mockupY);
    const first = bothY
      ? ((name.mockupY as number) <= (number.mockupY as number) ? name : number)
      : (name.sort <= number.sort ? name : number);
    const second = first === name ? number : name;
    const entry = (o: ZoneOption) => ({
      code: o.code,
      label: o.label,
      kind: (o === name ? 'name' : 'number') as DecorationKind,
    });
    return { view: name.view, ordered: [entry(first), entry(second)] };
  }
  return null;
}

// ---------------------------------------------------------------------------
// The wire  (port of [v2-slot-wire])
// ---------------------------------------------------------------------------
// submitlogo/lib/decoration-slots.js is the authoritative reader:
// `tier{N}DecorationSlots` is a JSON array of {code, kind} in ROW ORDER, plus
// one `tier{N}slot{i}File` per logo slot with i > 0.
//
// Slot 0 NEVER carries a file: its bytes are the tier's primary logo
// (tier{N}LogoFile / library / URL) and its stored path is mirrored to the legacy
// tier{N}LogoPath. stahlspreflight's resolveLogoSource, replacelogo and
// approveproof all depend on that invariant.

/** What one tier puts in `tier{N}DecorationSlots`. */
export function slotWirePayload(
  slots: IntakeSlot[],
): { code: string; kind: DecorationKind; font?: string; ink?: string; decoration?: { text: string; font?: string; textColor?: string } }[] {
  return (slots || []).map((s) => ({
    code: s.code,
    kind: s.kind,
    ...(s.kind === 'logo' && s.decoration?.text?.trim() ? {
      decoration: {
        text: s.decoration.text.trim(),
        ...(s.decoration.font ? { font: s.decoration.font } : {}),
        ...(s.decoration.textColor ? { textColor: s.decoration.textColor } : {}),
      },
    } : {}),
    ...(isTextFillKind(s.kind) && s.font ? { font: s.font } : {}),
    ...(isTextFillKind(s.kind) && s.ink ? { ink: resolvePrizeSlotInk(s.ink) } : {}),
  }));
}

/**
 * The first logo zone (i > 0) with no artwork, or null. Text zones carry none by
 * contract; slot 0's comes from the tier logo above.
 */
export function slotMissingFile(
  slots: IntakeSlot[],
  files: SlotFiles,
): { index: number; code: string } | null {
  for (let i = 1; i < (slots || []).length; i += 1) {
    if (slots[i].kind !== 'logo') continue;
    if (!(files || {})[i]) return { index: i, code: slots[i].code };
  }
  return null;
}

/** The multipart file fields this tier contributes, in row order. */
export function slotFileFields(
  tierN: number,
  slots: IntakeSlot[],
  files: SlotFiles,
): { field: string; index: number; file: File }[] {
  const out: { field: string; index: number; file: File }[] = [];
  for (let i = 1; i < (slots || []).length; i += 1) {
    if (slots[i].kind !== 'logo') continue;
    const file = (files || {})[i];
    if (file) out.push({ field: `tier${tierN}slot${i}File`, index: i, file });
  }
  return out;
}

/**
 * Append one tier's decoration fields to the outgoing multipart body.
 *
 * THE LEGACY GUARANTEE lives here: a tier that is not in zone mode (`slots` null
 * or empty — every un-migrated product) appends NOTHING AT ALL. Not an empty
 * `tier{N}DecorationSlots`, not a schema version. submitlogo takes its legacy
 * path whenever no slot field is sent, so the body stays byte-for-byte the
 * pre-slot-mode contract.
 */
export function appendTierDecoration(
  fd: FormData,
  tierN: number,
  slots: IntakeSlot[] | null | undefined,
  files: SlotFiles,
): void {
  if (!slots?.length) return;
  for (const sf of slotFileFields(tierN, slots, files)) fd.append(sf.field, sf.file);
  fd.append(`tier${tierN}DecorationSlots`, JSON.stringify(slotWirePayload(slots)));
}

// ---------------------------------------------------------------------------
// Slot-list edits. The dialog is a controlled component, so these return NEW
// arrays rather than mutating in place the way the admin form's handlers do.
// ---------------------------------------------------------------------------

/**
 * Whether every held slot is still something the CURRENT product can print.
 *
 * The dialog keeps the operator's zone choices in state while the product select
 * changes underneath them. A hoodie's Back Name is not a print area on a jogger,
 * and a Left Chest NUMBER stops being printable the moment the new product's LC
 * area has no font — both would 400 at intake. Any misfit means the tier's
 * choices belong to the previous garment and must be reseeded, not patched.
 */
export function slotsFitProduct(opts: ZoneOption[], slots: IntakeSlot[] | null | undefined): boolean {
  if (!slots?.length) return false;
  return slots.every((s) => {
    const o = opts.find((x) => x.code === s.code);
    return !!o && o.kinds.includes(s.kind);
  });
}

/**
 * Add a zone the picker actually offered. A stale chip (registry changed under
 * the open form) adds nothing rather than a code the operator cannot combine.
 * The zone opens on its first available kind.
 */
export function addZone(
  reg: DecorationRegistry | undefined,
  opts: ZoneOption[],
  slots: IntakeSlot[],
  code: string,
): IntakeSlot[] {
  const opt = zoneOfferable(reg, opts, slots).find((o) => o.code === code);
  if (!opt) return slots;
  const slot: IntakeSlot = { code: opt.code, kind: opt.kinds[0] };
  if (isTextFillKind(slot.kind)) {
    const t = slotTypographyDefaults(opt.area);
    slot.font = t.font;
    slot.ink = t.ink;
  }
  return [...slots, slot];
}

/** Add BOTH halves of the registry's stacked pair, in the order they sit on the garment. */
export function addStackPair(
  reg: DecorationRegistry | undefined,
  opts: ZoneOption[],
  slots: IntakeSlot[],
): IntakeSlot[] {
  const pair = zoneStackPair(reg, opts, slots);
  if (!pair) return slots;
  return [...slots, ...pair.ordered.map((z) => {
    const opt = opts.find((o) => o.code === z.code);
    const slot: IntakeSlot = { code: z.code, kind: z.kind };
    if (isTextFillKind(slot.kind)) {
      const t = slotTypographyDefaults(opt?.area);
      slot.font = t.font;
      slot.ink = t.ink;
    }
    return slot;
  })];
}

/**
 * Remove a row. File keys are slot INDICES, so they are compacted with the array
 * — otherwise the wrong artwork would ride along with the wrong slot. Slot 0 is
 * never removable (it is the tier's primary logo).
 */
export function removeSlot(
  slots: IntakeSlot[],
  files: SlotFiles,
  index: number,
): { slots: IntakeSlot[]; files: SlotFiles } {
  if (index <= 0 || index >= slots.length) return { slots, files };
  const nextSlots = slots.filter((_, i) => i !== index);
  const nextFiles: SlotFiles = {};
  for (const key of Object.keys(files || {}).map(Number).sort((a, b) => a - b)) {
    if (key < index) nextFiles[key] = files[key];
    else if (key > index) nextFiles[key - 1] = files[key];
  }
  return { slots: nextSlots, files: nextFiles };
}

/**
 * Re-point a row at a different zone. A location that can't do the current kind
 * re-picks its first kind instead of persisting an impossible pair, and drops
 * any artwork that is no longer applicable.
 */
export function setSlotCode(
  opts: ZoneOption[],
  slots: IntakeSlot[],
  files: SlotFiles,
  index: number,
  code: string,
): { slots: IntakeSlot[]; files: SlotFiles } {
  const opt = opts.find((o) => o.code === code);
  const next = slots.map((s, i) => (i === index ? { ...s, code } : s));
  let nextFiles = files;
  if (opt && !opt.kinds.includes(next[index].kind)) {
    next[index] = dropDecoration({ ...next[index], kind: opt.kinds[0] });
    if (next[index].kind !== 'logo') nextFiles = dropFile(files, index);
  }
  return { slots: next, files: nextFiles };
}

/** Change a row's content kind. A non-logo kind carries no artwork or text. */
export function setSlotKind(
  slots: IntakeSlot[],
  files: SlotFiles,
  index: number,
  kind: DecorationKind,
  area?: PrintArea,
): { slots: IntakeSlot[]; files: SlotFiles } {
  return {
    slots: slots.map((s, i) => {
      if (i !== index) return s;
      if (kind === 'logo') return dropTypography(dropDecoration({ ...s, kind }));
      const next = dropDecoration({ ...s, kind });
      return {
        ...next,
        font: resolvePrizeFont(next.font || area?.font),
        ink: resolvePrizeSlotInk(next.ink || area?.ink),
      };
    }),
    files: kind === 'logo' ? files : dropFile(files, index),
  };
}

export function setSlotFont(slots: IntakeSlot[], index: number, font: string): IntakeSlot[] {
  if (!isPrizeFont(font)) return slots;
  return slots.map((s, i) => (i === index && isTextFillKind(s.kind) ? { ...s, font } : s));
}

export function setSlotInk(slots: IntakeSlot[], index: number, ink: string): IntakeSlot[] {
  if (!isPrizeSlotInk(ink)) return slots;
  const next = resolvePrizeSlotInk(ink);
  return slots.map((s, i) => (i === index && isTextFillKind(s.kind) ? { ...s, ink: next } : s));
}

/** Prefill font/ink from the zone, else athletic-block / #FFFFFF. */
export function slotTypographyDefaults(area: PrintArea | undefined): { font: string; ink: string } {
  return {
    font: resolvePrizeFont(area?.font),
    ink: resolvePrizeSlotInk(area?.ink),
  };
}

function resolveCaptionStyle(
  style?: { font?: string; textColor?: string },
  existing?: IntakeSlot['decoration'],
): { font: string; textColor: string } {
  const fontRaw = style?.font || existing?.font || DEFAULT_CAPTION_FONT;
  const colorRaw = style?.textColor || existing?.textColor || DEFAULT_CAPTION_COLOR;
  const accepted = (ADDTEXT_FONTS as readonly string[]).includes(fontRaw) ? fontRaw : DEFAULT_CAPTION_FONT;
  const font = pickerFontValue(accepted);
  const trimmed = String(colorRaw || '').trim();
  const mode = trimmed.toLowerCase();
  const textColor = (mode === 'auto' || mode === 'auto-light')
    ? mode
    : (/^#[0-9a-fA-F]{6}$/.test(trimmed) ? trimmed.toUpperCase() : DEFAULT_CAPTION_COLOR);
  return { font, textColor };
}

/** Set (or clear, on empty text) the caption requested for ONE logo row. */
export function setSlotText(
  slots: IntakeSlot[],
  index: number,
  text: string,
  style?: { font?: string; textColor?: string },
): IntakeSlot[] {
  return slots.map((s, i) => {
    if (i !== index) return s;
    if (!text.trim()) return dropDecoration(s);
    if (s.kind !== 'logo') return s;
    const resolved = resolveCaptionStyle(style, s.decoration);
    return { ...s, decoration: { text: text.trim(), font: resolved.font, textColor: resolved.textColor } };
  });
}

export function setSlotCaptionStyle(
  slots: IntakeSlot[],
  index: number,
  style: { font?: string; textColor?: string },
): IntakeSlot[] {
  return slots.map((s, i) => {
    if (i !== index || s.kind !== 'logo' || !s.decoration?.text?.trim()) return s;
    const resolved = resolveCaptionStyle(style, s.decoration);
    return { ...s, decoration: { text: s.decoration.text.trim(), font: resolved.font, textColor: resolved.textColor } };
  });
}

function dropTypography(slot: IntakeSlot): IntakeSlot {
  const { font: _font, ink: _ink, ...rest } = slot;
  return rest;
}

function dropDecoration(slot: IntakeSlot): IntakeSlot {
  if (!slot.decoration) return slot;
  const { decoration: _decoration, ...rest } = slot;
  return rest;
}

function dropFile(files: SlotFiles, index: number): SlotFiles {
  if (!(index in (files || {}))) return files;
  const next = { ...files };
  delete next[index];
  return next;
}

export const SLOT_KIND_LABEL: Record<DecorationKind, string> = {
  logo: 'Logo',
  name: 'Player name',
  number: 'Player number',
  'name-number': 'Name and number',
};

export const SLOT_FILE_ACCEPT = '.png,.jpg,.jpeg,.pdf,.ai,.psd,.svg';
